/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import { IMultiLevelDatasetNode, NodeType } from '../../../../store/types/MultiLevelDatasetTypes';
import {
    IColumns, IGroups, IVisitNodeMapper, IVisitorContext
} from '../types/tableVisualizationConverterTypes';
import {
    IRow,
    ITableVisualizationDynamicProperties,
    ITableVisualizationStaticProperties
} from '../types/tableVisualizationTypes';
import { createDetailRow, createGroupRow, toActualNodeValues } from './rowFactory';

type RowCreator = (columns: IColumns, groups: IGroups, height: number, index: number) => IRow;

function fillContextWithRow(context: IVisitorContext, columns: IColumns, groups: IGroups, rowHeight: number, createRow: RowCreator) {
    const { table } = context;
    if (table && table.rows) {
        table.rows.push(createRow(columns, groups, rowHeight, context.rowsCount));
    }
}
// Group

function convertToGroup(
    node: IMultiLevelDatasetNode,
    _context: IVisitorContext,
    staticProperties: ITableVisualizationStaticProperties,
    dynamicProperties: ITableVisualizationDynamicProperties,
    startRowRenderIndex: number
): IVisitorContext {
    const context = _context;

    context.groupTitles.push(node.label);

    if (staticProperties.showTotals && !staticProperties.showDetails) {
        let skip = true;
        if (node.children.length && node.children[0].nodeType === NodeType.GROUP) {
            skip = node.children[0].children.length !== 0 && node.children[0].children[0].nodeType === NodeType.GROUP;
        }

        if (skip) {
            return context;
        }
    } else if (node.children.length && node.children[0].nodeType === NodeType.GROUP) {
        return context;
    }

    if (context.rowsCount >= startRowRenderIndex) {
        const rowIndex = startRowRenderIndex + context.addedRowsCount;
        const rowHeight = dynamicProperties.rowHeights[rowIndex] || -1;
        fillContextWithRow(
            context,
            { columns: staticProperties.columns, values: toActualNodeValues(staticProperties.columns, node.values, false) },
            { groups: staticProperties.groups, groupTitles: context.groupTitles },
            rowHeight,
            createGroupRow
        );
        context.addedRowsCount += 1;
    }

    context.rowsCount += 1;

    return context;
}

// Details

function convertToDetail(
    node: IMultiLevelDatasetNode,
    _context: IVisitorContext,
    staticProperties: ITableVisualizationStaticProperties,
    dynamicProperties: ITableVisualizationDynamicProperties,
    startRowRenderIndex: number
): IVisitorContext {
    const context = _context;

    if (staticProperties.showDetails) {
        if (context.rowsCount >= startRowRenderIndex) {
            const rowIndex = startRowRenderIndex + context.addedRowsCount;
            const rowHeight = dynamicProperties.rowHeights[rowIndex] || -1;
            fillContextWithRow(
                context,
                { columns: staticProperties.columns, values: toActualNodeValues(staticProperties.columns, node.values, true) },
                { groups: staticProperties.groups, groupTitles: context.groupTitles },
                rowHeight,
                createDetailRow
            );
            context.addedRowsCount += 1;
        }

        context.rowsCount += 1;
    }

    return context;
}

export default {
    [NodeType.GROUP]: convertToGroup,
    [NodeType.DETAIL]: convertToDetail
} as IVisitNodeMapper;
